import { addBreadcrumb, captureException } from '../../sentry-config'
import { Recipients } from '../../types/loft'
import { Item } from '../ews'
import { getAccessToken } from '../nested-app-auth/authHelper'
import { TokenOptions } from '../nested-app-auth/types'
import { mapRecipient, getGraphUrlMessages, getConvertedId, getGraphUrl } from './utils'
import { extractClaimsChallenge, readAuthenticateHeader } from './claimsChallenge'
import { GraphAuthChallengeError } from './errors'
import { getMessages } from '../../services/loft/messages'

const GRAPH_SCOPES = ['user.read', 'mail.read', 'mail.readwrite', 'mail.send', 'offline_access']

let accessToken: string

export const setAuthTokenGraphApi = async (token: string) => {
  accessToken = token
}

let tokenRequestInFlight: Promise<string> | null = null

let authChallengeReported = false

const acquireAccessToken = (options: TokenOptions = {}) => {
  if (!tokenRequestInFlight) {
    tokenRequestInFlight = getAccessToken(GRAPH_SCOPES, options)
      .then(token => {
        accessToken = token
        authChallengeReported = false
        return token
      })
      .finally(() => {
        tokenRequestInFlight = null
      })
  }

  return tokenRequestInFlight
}

const getCurrentAccessToken = async () => accessToken || acquireAccessToken()

const invalidateAccessToken = (rejectedToken: string) => {
  if (accessToken === rejectedToken) {
    accessToken = ''
  }
}

const refreshAccessToken = (staleToken: string, claimsChallenge?: string | null) => {
  if (accessToken && accessToken !== staleToken) {
    return Promise.resolve(accessToken)
  }

  invalidateAccessToken(staleToken)

  const options: TokenOptions = { forceRefresh: true, silentOnly: true }
  if (claimsChallenge) {
    options.claims = claimsChallenge
  }

  return acquireAccessToken(options)
}

type GraphRequestInit = Omit<RequestInit, 'headers'> & { headers?: Record<string, string> }

const authChallengeError = (claimsChallenge: string | null, cause?: unknown) => {
  const error = new GraphAuthChallengeError(
    getMessages().errorMessageAuthChallenge,
    claimsChallenge || undefined
  )

  if (!authChallengeReported) {
    authChallengeReported = true

    captureException(error, {
      extra: {
        hasClaimsChallenge: !!claimsChallenge,
        cause: cause?.toString() || '',
      },
      fingerprint: ['graphApi#authChallenge'],
    })
  }

  return error
}

/**
 * Performs a Graph request with the cached access token, recovering from a 401
 * once.
 */
const graphFetch = async (url: string, init: GraphRequestInit = {}): Promise<Response> => {
  const withAuth = (token: string) =>
    fetch(url, { ...init, headers: { ...init.headers, Authorization: `Bearer ${token}` } })

  const token = await getCurrentAccessToken()
  const response = await withAuth(token)

  if (response.status !== 401) {
    return response
  }

  const claimsChallenge = extractClaimsChallenge(readAuthenticateHeader(response))

  addBreadcrumb({
    category: 'graphApi',
    message: 'Graph API: 401, re-acquiring access token',
    level: 'warning',
    data: { url, hasClaimsChallenge: !!claimsChallenge },
  })

  let retriedToken: string
  let retriedResponse: Response

  try {
    retriedToken = await refreshAccessToken(token, claimsChallenge)
    retriedResponse = await withAuth(retriedToken)
  } catch (error) {
    throw authChallengeError(claimsChallenge, error)
  }

  if (retriedResponse.status === 401) {
    invalidateAccessToken(retriedToken)
    throw authChallengeError(claimsChallenge)
  }

  return retriedResponse
}

async function getMessageDetails(messageId: string) {
  const convertedId = getConvertedId(messageId)
  const url = `${getGraphUrlMessages()}/${convertedId}?$select=subject,toRecipients,ccRecipients,internetMessageId,hasAttachments`

  try {
    const response = await graphFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })

    const responseData = await response.json()

    if (!response.ok) {
      const errorCode = responseData.error?.code
      const errorMessage = responseData.error?.message || 'Unknown error'

      addBreadcrumb({
        category: 'graphApi',
        message: 'Loft: response not ok',
        level: 'error',
        data: {
          messageId,
          convertedId,
          errorCode,
          errorMessage,
          statusCode: response.status,
        },
      })

      if (errorCode !== 'ErrorItemNotFound' && response.status !== 404) {
        captureException(new Error(`Failed to get message details: ${errorMessage}`), {
          extra: {
            errorCode,
            messageId,
            convertedId,
            statusCode: response.status,
          },
        })
      }

      throw new Error(`Failed to get message details: ${errorMessage}`)
    }

    return responseData
  } catch (error) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Loft: Failed to get message details',
      level: 'error',
      data: { error: error?.toString() || '', messageId },
    })

    if (error instanceof GraphAuthChallengeError) {
      throw error
    }

    if (!(error instanceof Error && error.message.includes('Failed to get message details'))) {
      captureException(error)
    }

    throw error
  }
}

export async function waitSyncAndGetItemById(
  id: string,
  attemptsLeft = 10,
  retryDelay = 3000
): Promise<null | Item> {
  try {
    addBreadcrumb({ message: 'GraphApi: GetItem', category: 'graphApi' })

    return await getMessageDetails(id)
  } catch (error) {
    if (error instanceof GraphAuthChallengeError) {
      throw error
    }

    addBreadcrumb({
      message: `GraphApi: GetItem failed. Attempt left: ${attemptsLeft}`,
      level: 'error',
      category: 'graphApi',
      data: { error: error?.toString() || '', id },
    })
    captureException(error)
    if (attemptsLeft <= 1) {
      throw error
    }
    await new Promise(resolve => setTimeout(resolve, retryDelay))
    return waitSyncAndGetItemById(id, attemptsLeft - 1, retryDelay)
  }
}

export async function updateParentEmail(inReplyTo: string) {
  if (!inReplyTo) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Graph API: inReplyTo is empty',
    })
    return null
  }
  addBreadcrumb({
    category: 'graphApi',
    message: 'Graph API: updating parent email',
  })
  try {
    const letterAddressedBy: Item = await findItemByInternetMessageId(inReplyTo)
    if (!letterAddressedBy.id || !letterAddressedBy.changeKey) {
      addBreadcrumb({ message: 'GraphApi: Item not found', category: 'graphapi' })
      return
    }
    await updateReply(letterAddressedBy.id)
  } catch (error) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Loft: Failed to update parent',
      level: 'error',
      data: error?.toString() || '',
    })
  }
}

async function findItemByInternetMessageId(messageId: string) {
  addBreadcrumb({ message: 'GraphApi: Searching item', category: 'graphApi' })
  try {
    const folders = ['inbox', 'outbox', 'sentitems', 'msgfolderroot']

    const responses = await Promise.all(
      folders.map(folder =>
        graphFetch(
          `${getGraphUrl()}/me/mailFolders/${folder}/messages?$filter=contains(internetMessageId,'${messageId}')`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }
        ).then(res => res.json())
      )
    )
    const allMessages = responses.flatMap(res => res.value || [])
    const firstMatch = allMessages[0] || null
    return firstMatch
  } catch (error) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Loft: Failed to find message by Internet Message ID',
      level: 'error',
    })

    // Already captured with the challenge context by graphFetch.
    if (!(error instanceof GraphAuthChallengeError)) {
      captureException(error)
    }

    throw error
  }
}

export async function updateReply(id: string) {
  addBreadcrumb({ message: 'Graph API: Updating reply', category: 'graphApi' })
  try {
    const updatePayload = {
      singleValueExtendedProperties: [
        { id: 'Integer 0x1080', value: '261' },
        { id: 'Integer 0x1081', value: '103' },
        { id: 'SystemTime 0x1082', value: new Date().toISOString() },
      ],
    }
    const response = await graphFetch(`${getGraphUrl()}/me/messages/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatePayload),
    })
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Update failed: ${errorData.error.message}`)
    }
    return await response.json()
  } catch (error) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Loft: Failed to update reply',
      level: 'error',
      data: error?.toString() || '',
    })

    // Already captured with the challenge context by graphFetch.
    if (!(error instanceof GraphAuthChallengeError)) {
      captureException(error)
    }

    throw error
  }
}

export async function updateAndSendItem(
  id: string,
  body: string,
  subject: string,
  recipients: Recipients
) {
  addBreadcrumb({ message: 'Graph API: Sending item', category: 'graphApi' })
  const messages = getMessages()
  const maxRetries = 3
  let attempt = 0

  while (attempt < maxRetries) {
    try {
      const convertedId = getConvertedId(id)
      const messageUrl = `${getGraphUrlMessages()}/${convertedId}`

      const fetchResponse = await graphFetch(messageUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json()
        const errorCode = errorData.error?.code
        const errorMessage = errorData.error?.message || 'Unknown error'

        addBreadcrumb({
          category: 'graphApi',
          message: 'Graph API: Failed to fetch message for update',
          level: 'error',
          data: {
            errorCode,
            messageId: id,
            convertedId,
            statusCode: fetchResponse.status,
            attempt: attempt + 1,
          },
        })

        if (errorCode === 'ErrorItemNotFound' || fetchResponse.status === 404) {
          captureException(new Error(`Message not found: ${errorMessage}`), {
            extra: { errorCode, messageId: id, convertedId },
          })
          throw new Error(`Failed to fetch message: ${errorMessage}`)
        }

        throw new Error(`Failed to fetch message: ${errorMessage}`)
      }

      const messageData = await fetchResponse.json()
      const eTag = messageData['@odata.etag']

      const newBody = `<!DOCTYPE html><html><head></head><body>${body}</body></html>`

      const updatePayload = {
        subject,
        body: {
          content: newBody,
          contentType: 'HTML',
        },
        toRecipients: recipients.to.map(mapRecipient),
        ccRecipients: recipients.cc.map(mapRecipient),
        bccRecipients: recipients.bcc.map(mapRecipient),
      }

      const updateResponse = await graphFetch(messageUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'If-Match': eTag || '*',
        },
        body: JSON.stringify(updatePayload),
      })

      if (updateResponse.ok) {
        const sendResponse = await graphFetch(`${messageUrl}/send`, {
          method: 'POST',
        })

        if (!sendResponse.ok) {
          const errorData = await sendResponse.json()
          const errorCode = errorData.error?.code
          const errorMessage = `Send failed: ${errorData.error?.message || 'Unknown error'}`

          // Check for quota or throttling errors
          const isQuotaError =
            errorCode === 'ErrorQuotaExceeded' ||
            errorCode === 'RequestThrottled' ||
            errorCode === 'TooManyRequests'

          addBreadcrumb({
            category: 'graphApi',
            message: 'Graph API: Send failed',
            level: 'error',
            data: {
              errorCode,
              errorMessage: errorData.error?.message,
              attempt: attempt + 1,
              isQuotaError,
            },
          })

          if (isQuotaError && attempt < maxRetries - 1) {
            // Use exponential backoff for quota/throttling errors
            const retryAfter = errorData.error?.retryAfter || Math.pow(2, attempt) * 1000
            const delayMs =
              typeof retryAfter === 'number'
                ? retryAfter
                : (parseInt(retryAfter) || Math.pow(2, attempt)) * 1000

            const attemptNum = attempt + 1
            console.log(
              `[ErrorQuotaExceeded] Retrying after ${delayMs}ms (attempt ${attemptNum}/${maxRetries})`
            )

            addBreadcrumb({
              category: 'graphApi',
              message: `Graph API: Retrying after ${delayMs}ms due to ${errorCode}`,
              data: { delayMs, attempt: attempt + 1 },
            })

            await new Promise(resolve => setTimeout(resolve, delayMs))
            attempt++
            continue
          }

          captureException(new Error(errorMessage), {
            extra: {
              errorCode,
              attempt: attempt + 1,
              isQuotaError,
            },
          })
          throw new Error(errorMessage)
        }

        return { updated: true, messageId: id }
      }

      const errorData = await updateResponse.json()
      if (errorData.error?.code === 'ErrorIrresolvableConflict') {
        attempt++
        continue
      }

      throw new Error(`Update failed: ${errorData.error?.message || 'Unknown error'}`)
    } catch (error) {
      // Interaction is required, so the remaining attempts would all fail the same
      // way. Surface it instead of collapsing it into a generic send failure.
      if (error instanceof GraphAuthChallengeError) {
        throw error
      }

      if (attempt >= maxRetries - 1) {
        captureException(error)
        console.error(`Failed to update and send item after ${maxRetries} attempts:`, error)
        throw messages.errorMessageSendRetriesExhausted
      }
      attempt++
    }
  }

  throw new Error(messages.errorMessageSendRetriesExhausted)
}
